import { put, select } from "redux-saga/effects";

import { apiFetch } from "common/utilities";
import { trackSaga } from "common/utilities/trackSaga";
import { addDimensionToMapSuccess } from "domains/createMapDetail/actions";
import { getCurrentMap, getCurrentMapDimensionIds } from "domains/createMapDetail/selectors";
import { createCustomKeyIssueFailure, createCustomKeyIssueSuccess } from "domains/customKeyIssue/actions";
import type { CreateCustomKeyIssueDefaultAction } from "domains/customKeyIssue/actionTypes";
import { getDimensionUploadId } from "domains/customKeyIssue/selectors";
import type { DimensionIdPut, DimensionPost, NewCustomDimension } from "domains/dimension/dimension.types";
import { createTopicSelector } from "entities/topics/selectors";

type NewCustomDimensionWithTopicIds = Omit<NewCustomDimension, "topics"> & { topic_ids: string[] };

export const buildDimensionPost = (meta: NewCustomDimensionWithTopicIds, parentTopicId: string): DimensionPost => ({
  dimension: {
    description: meta.description,
    name: meta.name,
    parent_topic_id: parentTopicId,
    status: meta.status,
    subtitle: meta.subtitle,
    topic_ids: meta.topic_ids,
  },
});

export function* createCustomKeyIssueSaga({ meta }: CreateCustomKeyIssueDefaultAction) {
  try {
    const currentMap: ReturnType<typeof getCurrentMap> = yield select(getCurrentMap);
    if (currentMap?.id === undefined) throw new Error("currentMap id is undefined in createCustomKeyIssueSaga.");

    const existingDimensionId: ReturnType<typeof getDimensionUploadId> = yield select(getDimensionUploadId);

    const data = { ...meta, topic_ids: meta.topics.map((topic) => topic.id), topics: undefined };

    const dimensionId: string = yield existingDimensionId === undefined
      ? postDimension(data, currentMap.id)
      : putDimension(data, currentMap.id);

    const parentTopic: ReturnType<typeof createTopicSelector> = yield select(createTopicSelector(currentMap.id));
    if (parentTopic === undefined) throw new Error("parentTopic is undefined in createCustomKeyIssueSaga.");
    const idsOfKeyIssuesOnMap: ReturnType<typeof getCurrentMapDimensionIds> = yield select(getCurrentMapDimensionIds);

    if (!idsOfKeyIssuesOnMap.includes(dimensionId)) {
      yield put(addDimensionToMapSuccess(dimensionId));
      yield trackSaga({
        entityId: currentMap?.id,
        entityName: currentMap?.name ?? "(new map)",
        entityType: "Custom Map",
        name: "Add Topic to Map",
        page: "Create",
        secondaryEntityId: dimensionId,
        secondaryEntityType: "Topic",
      });
    }

    yield put(createCustomKeyIssueSuccess(dimensionId));
  } catch {
    yield put(createCustomKeyIssueFailure());
  }
}

function* postDimension(meta: NewCustomDimensionWithTopicIds, currentMapId: string) {
  const body = buildDimensionPost(meta, currentMapId);
  const response: { id: string } = yield apiFetch("dimension", { body, method: "POST" });
  return response.id;
}

function* putDimension(meta: NewCustomDimensionWithTopicIds, currentMapId: string) {
  const body: DimensionIdPut = {
    dimension: {
      description: meta.description,
      id: meta.id,
      image_url: meta.image_url,
      is_custom_dimension: meta.is_custom_dimension,
      name: meta.name,
      parent_topic_id: meta.parent_topic_id ?? currentMapId,
      status: meta.status,
      subtitle: meta.subtitle,
      topic_ids: meta.topic_ids,
    },
  };

  yield apiFetch(`dimension/${meta.id}`, { body, method: "PUT" });
  return meta.id;
}
